import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import registryService from '../services/registryService';
import { MAX_UPLOAD_BYTES } from '../utils/registrySourceConfig';
import { formatFileSize } from '../utils/fileUtils';
import { isSchemaFlagTrue, orderedPropertyEntries } from '../utils/registrySchemaUtils';
import { formatEntryValue } from '../utils/registryAttributes';
import {
  ACCEPT_IMPORT,
  IMPORT_OUTCOMES,
  importButtonLabel,
  outcomeCounts,
  resultText,
  writableCount,
} from '../utils/registryTransfer';
import { MONO_FONT } from '../constants/typography';
import { CONTENITORE_TABELLA, TESTATA_TABELLA } from './common/stiliTabella';

const TOO_BIG = 'Il file supera 50 MB. Dividilo.';
const STALE = "Il file è cambiato dopo l'anteprima: rifai l'anteprima";
const READ_FAILED = 'Non riesco a leggere il file: riprova tra poco.';
const SAMPLE_NOTE = 'Mostrate le prime 50 righe non valide e le prime 50 valide.';

// 412: il file e' cambiato dopo l'anteprima, si riparte dal passo 1 tenendo il file; il resto resta dove si e'.
const errorOf = (err) => {
  const message = err?.response?.data?.message;
  return err?.response?.status === 412
    ? { severity: 'warning', text: message || STALE }
    : { severity: 'error', text: message || READ_FAILED };
};

/** La riga degli esiti: un chip pieno per esito, col numero dentro; gli stessi colori tornano sulle righe. */
const OutcomeChips = ({ preview }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 2 }}>
    {outcomeCounts(preview).map(({ outcome, count, label, color }) => {
      const text = `${count} ${label}`;
      return (
        <Chip
          key={outcome}
          variant="filled"
          color={count === 0 ? 'default' : color}
          label={text}
          aria-label={text}
          sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        />
      );
    })}
  </Box>
);

// Nelle righe non valide i valori sono il testo grezzo del file: il formatter dello schema (ad esempio
// «Sì» per ogni testo non vuoto in un campo booleano) li farebbe sembrare validi.
const cellText = (row, def, name) => {
  const value = row.values?.[name];
  return row.outcome === 'INVALID' ? String(value ?? '') : formatEntryValue(def, value);
};

// Il campione ha solo righe non valide e da scrivere (le invariate sono solo contate): la nota serve
// quando una delle due categorie ha piu' righe di quelle mostrate.
const isSampleTruncated = (preview, rows) => {
  const invalidShown = rows.filter((row) => row.outcome === 'INVALID').length;
  const writableShown = rows.filter((row) => row.outcome === 'INSERT' || row.outcome === 'UPDATE').length;
  return preview.invalid > invalidShown || preview.toInsert + preview.toUpdate > writableShown;
};

const pluralize = (count, singular, plural) => (count === 1 ? singular : plural);

const SampleTable = ({ rows, schema }) => {
  const columns = orderedPropertyEntries(schema);
  return (
    <TableContainer {...CONTENITORE_TABELLA}>
      <Table size="small" aria-label="Anteprima delle righe">
        <TableHead>
          <TableRow sx={TESTATA_TABELLA}>
            <TableCell>Riga</TableCell>
            <TableCell>Esito</TableCell>
            {columns.map(([name]) => <TableCell key={name}>{name}</TableCell>)}
            <TableCell>Motivo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const outcome = IMPORT_OUTCOMES[row.outcome] || { single: row.outcome, color: 'default' };
            return (
              <TableRow key={`${row.line}-${row.outcome}`}>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.line}</TableCell>
                <TableCell>
                  <Chip size="small" variant="filled" color={outcome.color} label={outcome.single} />
                </TableCell>
                {columns.map(([name, def]) => (
                  <TableCell
                    key={name}
                    sx={isSchemaFlagTrue(def?.key) ? { fontFamily: MONO_FONT, fontWeight: 500 } : undefined}
                  >
                    {cellText(row, def, name) || '—'}
                  </TableCell>
                ))}
                <TableCell>{(row.errors || []).map((e) => `${e.field} — ${e.message}`).join('; ')}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const FileStep = ({ registry, file, tooBig, busy, downloading, onChoose, onDownloadTemplate }) => {
  const names = orderedPropertyEntries(registry.schema).map(([name]) => name).join(', ');
  return (
    <>
      <Typography variant="body2">
        {`Il file deve avere una colonna per ogni campo, con lo stesso nome (${names}). `
          + 'Scarica il modello Excel per partire.'}
      </Typography>
      <Box sx={{ mt: 1 }}>
        <Button variant="outlined" size="small" onClick={onDownloadTemplate} disabled={busy || downloading}>
          Scarica il modello
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
        Nei CSV un apostrofo davanti a = + - @ serve a mostrare il valore come testo e viene tolto all&apos;import.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2.5 }}>
        <Button component="label" variant="outlined" disabled={busy}>
          Scegli file
          <input
            type="file"
            accept={ACCEPT_IMPORT}
            hidden
            aria-label="File da importare"
            onChange={onChoose}
          />
        </Button>
        {file && (
          <Typography variant="body2" color="text.secondary">
            {file.name} · {formatFileSize(file.size)}
          </Typography>
        )}
      </Box>
      {tooBig && <FormHelperText error>{TOO_BIG}</FormHelperText>}
    </>
  );
};

const PreviewStep = ({ preview, schema, skipInvalid, onSkipChange }) => {
  const { blockingErrors = [], ignoredColumns = [], missingOptionalColumns = [], rows = [] } = preview;
  return (
    <>
      <Typography variant="body2" color="text.secondary">
        {preview.fileName} · {preview.totalRows} {pluralize(preview.totalRows, 'riga', 'righe')}
      </Typography>
      <OutcomeChips preview={preview} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {blockingErrors.map((message) => <Alert key={message} severity="error">{message}</Alert>)}
        {blockingErrors.length > 0 && <Typography variant="body2">Correggi il file e ricarica.</Typography>}
        {ignoredColumns.length > 0 && (
          <Alert severity="info">Colonne ignorate: {ignoredColumns.join(', ')}</Alert>
        )}
        {missingOptionalColumns.length > 0 && (
          <Alert severity="warning">
            {missingOptionalColumns.length === 1
              ? `Colonna facoltativa assente: ${missingOptionalColumns[0]}. Nelle voci aggiornate il campo viene svuotato.`
              : `Colonne facoltative assenti: ${missingOptionalColumns.join(', ')}. `
                + 'Nelle voci aggiornate i campi vengono svuotati.'}
          </Alert>
        )}
      </Box>
      {rows.length > 0 && <SampleTable rows={rows} schema={schema} />}
      {isSampleTruncated(preview, rows) && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>{SAMPLE_NOTE}</Typography>
      )}
      {preview.invalid > 0 && (
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Checkbox checked={skipInvalid} onChange={(e) => onSkipChange(e.target.checked)} />}
          label={preview.invalid === 1
            ? 'Importa comunque, saltando la riga non valida'
            : `Importa comunque, saltando le ${preview.invalid} righe non valide`}
        />
      )}
    </>
  );
};

const ImportBody = ({ registry, onClose, onWritten, onBusyChange, onDownloadTemplate }) => {
  const [step, setStep] = useState('file');
  const [file, setFile] = useState(null);
  const [tooBig, setTooBig] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [skipInvalid, setSkipInvalid] = useState(false);
  const [busy, setBusyState] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // lo stato occupato e' anche del wrapper: Esc e sfondo non chiudono mentre una chiamata e' in corso
  const setBusy = (value) => {
    setBusyState(value);
    onBusyChange(value);
  };

  const handleChoose = (event) => {
    const chosen = event.target.files?.[0] || null;
    // azzera l'input: lo stesso file si puo' riscegliere dopo una correzione
    event.target.value = '';
    if (!chosen) return;
    const over = chosen.size > MAX_UPLOAD_BYTES;
    setTooBig(over);
    setFile(over ? null : chosen);
    setError(null);
  };

  // Il modello lo scarica la pagina; se fallisce, il messaggio (gia' leggibile) va mostrato qui:
  // l'avviso della pagina sta dietro a questo dialogo.
  const downloadTemplate = async () => {
    setDownloading(true);
    setError(null);
    try {
      await onDownloadTemplate?.();
    } catch (err) {
      setError({ severity: 'error', text: err?.message || READ_FAILED });
    } finally {
      setDownloading(false);
    }
  };

  const showPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      setPreview(await registryService.previewImport(registry.id, file));
      setSkipInvalid(false);
      setStep('preview');
    } catch (err) {
      setError(errorOf(err));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const written = await registryService.importEntries(registry.id, file, {
        fingerprint: preview.fingerprint,
        skipInvalid,
      });
      onWritten(written);
      setResult(written);
      setStep('done');
    } catch (err) {
      setError(errorOf(err));
      if (err?.response?.status === 412) {
        setPreview(null);
        setStep('file');
      }
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    setError(null);
    setPreview(null);
    setStep('file');
  };

  const importDisabled = busy
    || !preview
    || preview.blockingErrors?.length > 0
    || (preview.invalid > 0 && !skipInvalid)
    || writableCount(preview) === 0;

  return (
    <>
      <DialogContent>
        {error && <Alert severity={error.severity} sx={{ mb: 2 }}>{error.text}</Alert>}
        {step === 'file' && (
          <FileStep
            registry={registry}
            file={file}
            tooBig={tooBig}
            busy={busy}
            downloading={downloading}
            onChoose={handleChoose}
            onDownloadTemplate={downloadTemplate}
          />
        )}
        {step === 'preview' && (
          <PreviewStep
            preview={preview}
            schema={registry.schema}
            skipInvalid={skipInvalid}
            onSkipChange={setSkipInvalid}
          />
        )}
        {step === 'done' && <Alert severity="success">{resultText(result)}</Alert>}
      </DialogContent>
      <DialogActions>
        {step === 'file' && (
          <>
            <Button onClick={onClose} disabled={busy}>Annulla</Button>
            <Button variant="contained" onClick={showPreview} disabled={!file || busy}>
              {busy ? 'Analisi in corso…' : 'Mostra anteprima'}
            </Button>
          </>
        )}
        {step === 'preview' && (
          <>
            <Button onClick={back} disabled={busy}>Indietro</Button>
            <Button variant="contained" onClick={confirm} disabled={importDisabled}>
              {busy ? 'Importazione in corso…' : importButtonLabel(preview)}
            </Button>
          </>
        )}
        {step === 'done' && <Button variant="contained" onClick={onClose}>Chiudi</Button>}
      </DialogActions>
    </>
  );
};

/** Importa voci in un'anagrafica interna: scegli il file, controlla l'anteprima, conferma. */
const RegistryImportDialog = ({ open, registry, onClose, onImported, onDownloadTemplate }) => {
  // l'esito resta qui: qualunque via di chiusura (Chiudi, Esc, sfondo) dopo la scrittura avvisa la pagina
  const written = useRef(null);
  const busy = useRef(false);
  // a ogni apertura si riparte puliti: niente esito o occupato rimasti da una chiusura precedente
  useEffect(() => {
    if (open) {
      written.current = null;
      busy.current = false;
    }
  }, [open]);

  const close = () => {
    const result = written.current;
    written.current = null;
    if (result) onImported?.(result);
    onClose();
  };

  return (
    <Dialog open={!!open} onClose={() => { if (!busy.current) close(); }} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box component="span">Importa voci · {registry?.name || registry?.code}</Box>
      </DialogTitle>
      {/* il corpo si monta a ogni apertura: chiudere e riaprire azzera lo stato */}
      {open && registry && (
        <ImportBody
          registry={registry}
          onClose={close}
          onBusyChange={(value) => { busy.current = value; }}
          onWritten={(result) => { written.current = result; }}
          onDownloadTemplate={onDownloadTemplate}
        />
      )}
    </Dialog>
  );
};

export default RegistryImportDialog;
